import React, { useContext } from 'react';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { postRequest } from '../../../api/ApiController';
import UtilContext from '../../../components/UtilContext';
import { LocaleContext } from '../../../components/LocaleContext';
import { SEMAFOR_COLOR_OPTIONS, getSemaforColorClass } from '../../../utils/moduleSettings';
import { SelectButton } from 'primereact/selectbutton';
import { Dropdown } from 'primereact/dropdown';
import { uiT } from '../../../utils/uiTranslate';

const COLOR_VALUES = ['red', 'orange', 'green', 'gray', 'blue', 'black'];

const SEMAFOR_KEYS = [
  { key: 'otk_semafor_0', label: uiT('Common.Expiry.AfterRange') },
  { key: 'otk_semafor_15', label: uiT('Common.Expiry.Days15Range') },
  { key: 'otk_semafor_30', label: uiT('Common.Expiry.Days30Range') },
  { key: 'otk_semafor_45', label: uiT('Common.Expiry.Days45Range') },
  { key: 'otk_semafor_60', label: uiT('Common.Expiry.Days60Range') },
  { key: 'otk_semafor_60_plus', label: uiT('Common.Expiry.Days61Plus') },
  { key: 'otk_semafor_dea', label: uiT('Otk.DEA.Deactivated') },
];

const GRAPH_OPEN_CLOSED_KEYS = [
  { key: 'otk_graph_open_color', label: uiT('Status.OpenPlural') },
  { key: 'otk_graph_closed_color', label: uiT('Status.ClosedNeut') },
];

const GRAPH_DOCS_KEYS = [
  { key: 'otk_graph_docs_0', label: uiT('Common.Expiry.After') },
  { key: 'otk_graph_docs_15', label: uiT('Common.Expiry.Days15Short') },
  { key: 'otk_graph_docs_30', label: uiT('Common.Expiry.Days30Short') },
  { key: 'otk_graph_docs_ok', label: 'OK' },
];

function getSetting(module_settings, key, defaultValue = '0') {
  const row = (module_settings || []).find(s => s.module === 'otk' && s.key === key);
  const v = row ? row.value : defaultValue;
  if (v == null || v === '') return defaultValue;
  return (v === 0 || v === '0') ? '0' : (v === 1 || v === '1') ? '1' : String(v);
}

export default function OtkGeneralSettings() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { toast } = useContext(UtilContext);
  const { t } = useContext(LocaleContext);
  const OPTIONS = [
    { label: t('OtkGeneralSettings.Option.Off'), value: '0' },
    { label: t('OtkGeneralSettings.Option.On'), value: '1' },
  ];

  const module_settings = state.module_settings || [];
  const excludedByCompany = getSetting(module_settings, 'show_excluded_partners_list_by_company');
  const excludedByState = getSetting(module_settings, 'show_excluded_partners_list_by_state');
  const deactivatedDocsByCompany = getSetting(module_settings, 'show_deactivated_docs_list_by_company');
  const deactivatedDocsByState = getSetting(module_settings, 'show_deactivated_docs_list_by_state');

  const settingExcludedByCompany = module_settings.find(s => s.module === 'otk' && s.key === 'show_excluded_partners_list_by_company');
  const settingExcludedByState = module_settings.find(s => s.module === 'otk' && s.key === 'show_excluded_partners_list_by_state');
  const settingDeactivatedByCompany = module_settings.find(s => s.module === 'otk' && s.key === 'show_deactivated_docs_list_by_company');
  const settingDeactivatedByState = module_settings.find(s => s.module === 'otk' && s.key === 'show_deactivated_docs_list_by_state');

  async function updateSetting(setting, value, currentValue) {
    const normalized = value === 0 || value === '0' ? '0' : (value === 1 || value === '1' ? '1' : null);
    if (normalized === null) return;
    const curNorm = currentValue === 0 || currentValue === '0' ? '0' : '1';
    if (normalized === curNorm) return;
    if (!setting) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('OtkGeneralSettings.Toast.SettingMissing') });
      return;
    }
    try {
      await postRequest(`/api/module_settings/update/${setting.id}`, { value: normalized }, true);
      dispatch({ type: 'update_module_setting', id: setting.id, value: normalized });
      toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('OtkGeneralSettings.Toast.ValueChanged') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('OtkGeneralSettings.Toast.SaveFailed') });
    }
  }

  async function updateSemaforSetting(setting, value, currentValue) {
    if (!setting) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('OtkGeneralSettings.Toast.SettingMissing') });
      return;
    }
    if (!COLOR_VALUES.includes(value)) return;
    if (value === currentValue) return;
    try {
      await postRequest(`/api/module_settings/update/${setting.id}`, { value }, true);
      dispatch({ type: 'update_module_setting', id: setting.id, value });
      toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('OtkGeneralSettings.Toast.SemaphoreColorChanged') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('OtkGeneralSettings.Toast.SaveFailed') });
    }
  }

  async function updateGraphColorSetting(setting, value, currentValue) {
    if (!setting) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('OtkGeneralSettings.Toast.SettingMissing') });
      return;
    }
    if (!COLOR_VALUES.includes(value)) return;
    if (value === currentValue) return;
    try {
      await postRequest(`/api/module_settings/update/${setting.id}`, { value }, true);
      dispatch({ type: 'update_module_setting', id: setting.id, value });
      toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('OtkGeneralSettings.Toast.ColorChanged') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('OtkGeneralSettings.Toast.SaveFailed') });
    }
  }

  return (
    <div className="relative flex flex-col h-[calc(100vh-137px)] bg-gray-100 overflow-y-auto overflow-x-hidden">
      <div className="p-4 w-full">
        <h1 className="text-3xl font-semibold text-left text-zinc-900 mb-4">
          {t('OtkGeneralSettings.Heading')}
        </h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 w-full items-stretch">
          {/* Karta 1: Všeobecné nastavenia */}
          <div className="p-6 bg-white rounded-md shadow-md flex flex-col min-h-0">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">{t('Ui.R67.Zobrazenie_vyradenych_obchodnych_partnerov_v_zoznamoch')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Vyradeni_obchodni_partneri_sa_standardne_v_zoznamoch_Zoznam_')}</p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200 mb-6">
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Zoznam_platnosti_podla_firmy')}</label>
                <div className="shrink-0 min-w-[140px]">
                  <SelectButton
                    value={excludedByCompany}
                    onChange={(e) => updateSetting(settingExcludedByCompany, e.value, excludedByCompany)}
                    options={OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Zoznam_platnosti_podla_stavu')}</label>
                <div className="shrink-0 min-w-[140px]">
                  <SelectButton
                    value={excludedByState}
                    onChange={(e) => updateSetting(settingExcludedByState, e.value, excludedByState)}
                    options={OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
            </div>

            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200 mt-2">{t('Ui.R67.Zobrazenie_deaktivovanych_dokumentacii_v_zoznamoch')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Dokumentacie_so_zadanym_datumom_deaktivacie_sa_standardne_v_')}</p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200">
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Zoznam_platnosti_podla_firmy')}</label>
                <div className="shrink-0 min-w-[140px]">
                  <SelectButton
                    value={deactivatedDocsByCompany}
                    onChange={(e) => updateSetting(settingDeactivatedByCompany, e.value, deactivatedDocsByCompany)}
                    options={OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Zoznam_platnosti_podla_stavu')}</label>
                <div className="shrink-0 min-w-[140px]">
                  <SelectButton
                    value={deactivatedDocsByState}
                    onChange={(e) => updateSetting(settingDeactivatedByState, e.value, deactivatedDocsByState)}
                    options={OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Karta 2: Farby pre zoznamy */}
          <div className="p-6 bg-white rounded-md shadow-md flex flex-col min-h-0">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">
              Farby pre zoznamy
            </h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Tieto_farby_sa_pouzivaju_v_zoznamoch_kvality_semafor_expirac')}</p>
            <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
              {SEMAFOR_KEYS.map(({ key, label }) => {
                const setting = module_settings.find(s => s.module === 'otk' && s.key === key);
                const defaultValue = key === 'otk_semafor_0' ? 'red' : (key === 'otk_semafor_60_plus' ? 'green' : (key === 'otk_semafor_dea' ? 'gray' : 'orange'));
                const currentValue = getSetting(module_settings, key, defaultValue);
                const colorClass = getSemaforColorClass(currentValue);
                return (
                  <div key={key} className="grid grid-cols-1 sm:grid-cols-[minmax(200px,1fr)_220px] gap-3 items-center py-1.5">
                    <label className="text-sm font-medium text-gray-800 truncate">{label}</label>
                    <div className="flex items-center gap-2 shrink-0 min-w-0">
                      <span className={`inline-block w-5 h-5 rounded-full shrink-0 ${colorClass}`} title={currentValue} aria-hidden />
                      <Dropdown
                        value={currentValue}
                        options={SEMAFOR_COLOR_OPTIONS}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => updateSemaforSetting(setting, e.value, currentValue)}
                        className="w-[180px] border border-gray-400 rounded"
                        placeholder={uiT('Common.Color')}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Karta 3: Farby pre grafy v náhľade kvality */}
          <div className="p-6 bg-white rounded-md shadow-md flex flex-col min-h-0">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">{t('Ui.R67.Farby_pre_grafy_v_nahlade_kvality')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Nastavenie_farieb_grafov_na_stranke_Nahlad_kvality')}</p>

            <div className="mb-6">
              <h3 className="text-base font-semibold text-zinc-700 mb-2">{t('Ui.R67.INT_EXT_REK_Otvorene_Zatvorene')}</h3>
              <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
                {GRAPH_OPEN_CLOSED_KEYS.map(({ key, label }) => {
                  const setting = module_settings.find(s => s.module === 'otk' && s.key === key);
                  const defaultValue = key === 'otk_graph_open_color' ? 'green' : 'gray';
                  const currentValue = getSetting(module_settings, key, defaultValue);
                  const colorClass = getSemaforColorClass(currentValue);
                  return (
                    <div key={key} className="grid grid-cols-1 sm:grid-cols-[minmax(200px,1fr)_220px] gap-3 items-center py-1.5">
                      <label className="text-sm font-medium text-gray-800 truncate">{label}</label>
                      <div className="flex items-center gap-2 shrink-0 min-w-0">
                        <span className={`inline-block w-5 h-5 rounded-full shrink-0 ${colorClass}`} title={currentValue} aria-hidden />
                        <Dropdown
                          value={currentValue}
                          options={SEMAFOR_COLOR_OPTIONS}
                          optionLabel="label"
                          optionValue="value"
                          onChange={(e) => updateGraphColorSetting(setting, e.value, currentValue)}
                          className="w-[180px] border border-gray-400 rounded"
                          placeholder={uiT('Common.Color')}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="text-base font-semibold text-zinc-700 mb-2">{t('Ui.R67.Dokumentacie')}</h3>
              <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
                {GRAPH_DOCS_KEYS.map(({ key, label }) => {
                  const setting = module_settings.find(s => s.module === 'otk' && s.key === key);
                  const defaultValue = key === 'otk_graph_docs_ok' ? 'green' : (key === 'otk_graph_docs_0' ? 'red' : 'orange');
                  const currentValue = getSetting(module_settings, key, defaultValue);
                  const colorClass = getSemaforColorClass(currentValue);
                  return (
                    <div key={key} className="grid grid-cols-1 sm:grid-cols-[minmax(200px,1fr)_220px] gap-3 items-center py-1.5">
                      <label className="text-sm font-medium text-gray-800 truncate">{label}</label>
                      <div className="flex items-center gap-2 shrink-0 min-w-0">
                        <span className={`inline-block w-5 h-5 rounded-full shrink-0 ${colorClass}`} title={currentValue} aria-hidden />
                        <Dropdown
                          value={currentValue}
                          options={SEMAFOR_COLOR_OPTIONS}
                          optionLabel="label"
                          optionValue="value"
                          onChange={(e) => updateGraphColorSetting(setting, e.value, currentValue)}
                          className="w-[180px] border border-gray-400 rounded"
                          placeholder={uiT('Common.Color')}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
